import { supabase } from '@/services/supabase/supabase';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useProductStore } from './useProductStore';

export interface ProductMeta {
  user: {
    id: string;
    email?: string;
    fullName?: string;
    avatarUrl?: string;
    preferences?: Record<string, any>;
  } | null;
  tenant: {
    id: string;
    name?: string;
    role?: string;
    plan?: string;
  } | null;
  subscription: {
    plan_id: string;
    status: string;
    current_period_end?: string;
    plan_details?: any; // To store the joined plan data
  } | null;
  credits: number;
  isLoading: boolean;
  error: Error | null;
}

const PRODUCT_META_KEY = ['meta', 'product'];

export function useProductMeta(): ProductMeta {
  const setProductMeta = useProductStore((state) => state.setProductMeta);

  const { data, isLoading, error } = useQuery({
    queryKey: PRODUCT_META_KEY,
    queryFn: async () => {
      // 1. Get User Session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      const user = session?.user;
      if (!user) return { user: null, tenant: null, subscription: null, credits: 0 };

      // 2. Derive User Details from Metadata
      const userMeta = user.user_metadata || {};

      // 3. Derive Tenant Details
      // Strictly fetch from database (user_tenants)
      let tenantId = null;

      if (user.id) {
        try {
          const { data: dbTenant } = await supabase
            .from('user_tenants')
            .select('tenant_id')
            .eq('user_id', user.id)
            .maybeSingle();

          if (dbTenant) {
            tenantId = dbTenant.tenant_id;
          }
        } catch (err) {
          console.warn('Failed to fetch tenant from DB:', err);
        }
      }

      let tenant = null;
      if (tenantId) {
        tenant = {
          id: tenantId,
          // Plan and role are currently not available in metadata/claims. 
          // They would need a separate DB query if needed.
          name: undefined,
          plan: undefined,
          role: 'member' // Default role
        };
      }

      // 4. Fetch Subscription and Credits (if tenant exists)
      let subscription = null;
      let credits = 0;

      if (tenantId) {
        try {
          // Fetch Subscription
          const { data: subData } = await supabase
            .from('subscriptions')
            .select('*, plans(*)')
            .eq('tenant_id', tenantId)
            .maybeSingle();

          if (subData) {
            subscription = {
              plan_id: subData.plan_id,
              status: subData.status,
              current_period_end: subData.current_period_end,
              plan_details: subData.plans
            };
          }

          // Fetch Credits (Latest Balance)
          const { data: creditData } = await supabase
            .from('credits_ledger')
            .select('balance_after')
            .eq('tenant_id', tenantId)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (creditData) {
            credits = creditData.balance_after;
          }
        } catch (err) {
          console.warn('Failed to fetch subscription/credits:', err);
        }
      }

      return {
        user: {
          id: user.id,
          email: user.email,
          fullName: userMeta.full_name || userMeta.name,
          avatarUrl: userMeta.avatar_url || userMeta.picture || userMeta.avatar,
          preferences: userMeta.preferences
        },
        tenant,
        subscription,
        credits
      };
    },
    staleTime: 1000 * 60 * 60, // 1 hour - user/tenant data rarely changes
  });

  const result = {
    user: data?.user || null,
    tenant: data?.tenant || null,
    subscription: data?.subscription || null,
    credits: data?.credits || 0,
    isLoading,
    error: error as Error | null
  };

  useEffect(() => {
    setProductMeta(result);
  }, [data, isLoading, error, setProductMeta]);

  return result;
}

